import {
  type ConfigFieldDescriptor,
  type ConfigFieldKey,
  MERCHANT_RISK_CONFIG_FIELDS,
  RISK_CONFIG_FIELDS,
} from "@unbox/api";
import type { RiskModelType } from "@unbox/db";

// Pretty names for the risk_model_type_enum.
export const MODEL_TYPE_LABELS: Record<RiskModelType, string> = {
  debate: "Debate",
  claims: "Claims",
  omni: "Omni",
  keyword: "Keyword",
  required_text: "Required text",
  music: "Music",
  ai_content: "AI content",
};

// The only two merchant-level fields that inherit from the Risk Catalog until
// explicitly overridden (UNB-2723) — every other field is either catalog-level
// (no inheritance to show) or already communicates it via its own hint (`threshold`).
export const INHERITANCE_BADGE_FIELDS = new Set<ConfigFieldKey>(["description", "hardcoded_rules"]);

// A renderable config field is exactly the API descriptor (key + type); the
// `string` type renders as a textarea, `number` as a numeric input, `stringArray`
// as a line-per-item textarea. Label/placeholder come from CONFIG_FIELD_META.
export type ConfigField = ConfigFieldDescriptor;

// Field lists straight from the API schemas — single source of truth, no drift.
export const MERCHANT_CONFIG_FIELDS = MERCHANT_RISK_CONFIG_FIELDS;
export const CATALOG_CONFIG_FIELDS = RISK_CONFIG_FIELDS;

// Presentation per config field. Exhaustive over the API's ConfigFieldKey union,
// so adding a field to a schema is a compile error here until it's given a label,
// placeholder, and hint.
export const CONFIG_FIELD_META: Record<ConfigFieldKey, { label: string; placeholder: string; hint: string }> = {
  hardcoded_rules: {
    label: "Exceptions & extra rules",
    placeholder: "e.g. Do not flag claims on product packaging or label…",
    hint:
      "Extra instructions added after the definition — typically exceptions ('don't flag claims on " +
      "packaging') or merchant-specific tightening. Plain language, sent to the AI with the definition " +
      "above. (Previously called Hardcoded rules.)",
  },
  description: {
    label: "What to detect",
    placeholder: "What this risk detects… Use {key} to insert a merchant variable, e.g. {brand_name}.",
    hint:
      "Describes the risk to the AI in plain language — what in a video should trigger a flag. " +
      "Overrides the catalog default for this merchant. Supports {variable} tokens filled from " +
      "Merchant variables, e.g. {own_brands} or {product_name}. (Previously called LLM description.)",
  },
  approved_claims: {
    label: "Approved claims",
    placeholder: "One approved claim per line",
    hint:
      "The allowlist of claims creators are allowed to make for this merchant — anything outside it " +
      "can be flagged. Merchant-editable in the app.",
  },
  banned_keywords: {
    label: "Banned keywords",
    placeholder: "One banned keyword per line",
    hint: "Words or phrases that flag a video when they appear. Merchant-editable in the app.",
  },
  expected_strings: {
    label: "Expected strings",
    placeholder: "One required string per line",
    hint:
      "Hashtags or overlay text that must appear in the video — the risk flags when they're missing. " +
      "Merchant-editable in the app.",
  },
  system_prompt: {
    label: "System prompt",
    placeholder: "Instructions for the claims model…",
    hint: "Catalog-level — applies to every merchant using this risk.",
  },
  omni_risk_name: {
    label: "Omni risk name",
    placeholder: "e.g. child_appeal",
    hint: "Catalog-level — the omni model's risk this maps to. Applies to every merchant using this risk.",
  },
  threshold: {
    label: "Tolerance (threshold)",
    placeholder: "0–1",
    hint:
      "The omni model score above which the risk fires. The Risk Catalog value is the default for every " +
      "merchant; a value in a merchant's Safety Configuration overrides it for that merchant only — leave " +
      "empty to use the catalog default.",
  },
  confidence_threshold: {
    label: "Confidence threshold",
    placeholder: "0–1",
    hint: "Catalog-level — the debate confidence above which the risk fires. Applies to every merchant using this risk.",
  },
};

// Build a complete, schema-valid default config from a field descriptor list.
// Optional fields are left out — absent means "no override".
export const defaultConfigFromFields = (fields: ConfigField[]): Record<string, unknown> => {
  const config: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.optional) continue;
    config[field.key] = field.type === "stringArray" ? [] : field.type === "number" ? 0 : "";
  }
  return config;
};

// Construct a config object with EXACTLY the given field keys, seeding from any
// stored values. Drops unknown keys so re-saving a row with legacy keys won't
// fail strict validation.
export const buildConfigFromFields = (
  fields: ConfigField[],
  stored: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  const base = defaultConfigFromFields(fields);
  if (!stored) return base;
  for (const field of fields) {
    if (stored[field.key] !== undefined) base[field.key] = stored[field.key];
  }
  return base;
};

/** Parse a line-per-item textarea into trimmed, non-empty items. */
export const parseStringArray = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

// Fields that fall back to the catalog value when the merchant hasn't overridden them —
// stored as null rather than "" so the fallback (and the inheritance badge) can tell "never
// touched" apart from "explicitly emptied" (UNB-2723).
const NULLABLE_ON_EMPTY_FIELDS: ConfigFieldKey[] = ["description", "hardcoded_rules"];

// Serialize a config for saving: an emptied nullable field persists as null rather than "".
// Everything else is unchanged.
export const serializeConfigForSave = (config: Record<string, unknown>): Record<string, unknown> => {
  const result = { ...config };
  for (const key of NULLABLE_ON_EMPTY_FIELDS) {
    if (result[key] === "") result[key] = null;
  }
  return result;
};

/** Derive a stable snake_case risk id from a readable name. */
export const slugifyRiskId = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
